import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import { supabase } from '../lib/supabase';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const REDIRECT_SECONDS = 8;
const VERIFY_TIMEOUT_MS = 8000;

type Status = 'loading' | 'success' | 'error';

const BlueprintShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative isolate flex min-h-screen w-full items-center justify-center bg-white px-6 py-16">
    <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />
    <div className="relative z-10 w-full max-w-sm">{children}</div>
  </div>
);

const EmailVerified: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [errorDetail, setErrorDetail] = useState('');
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  // Resolve verification: Supabase processes the token from the URL
  // asynchronously, so listen for the auth event instead of checking once.
  useEffect(() => {
    // Expired/invalid links come back with error params in the URL hash.
    const params = new URLSearchParams(
      window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.search,
    );
    const urlError = params.get('error_description') || params.get('error');
    if (urlError) {
      setErrorDetail(urlError.replace(/\+/g, ' '));
      setStatus('error');
      return;
    }

    let resolved = false;

    const succeed = () => {
      if (resolved) return;
      resolved = true;
      setStatus('success');
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
        succeed();
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) succeed();
    });

    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        setStatus('error');
      }
    }, VERIFY_TIMEOUT_MS);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Auto-redirect after success.
  useEffect(() => {
    if (status !== 'success') return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/arena');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [status, navigate]);

  if (status === 'loading') {
    return (
      <MotionConfig reducedMotion="user">
        <BlueprintShell>
          <div className="text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
              Steelman
            </span>
            <div className="mx-auto mt-6 h-10 w-10 animate-spin rounded-full border-2 border-hairline border-t-charcoal" />
            <p className="mt-5 text-sm text-ink-muted">Verifying your email…</p>
          </div>
        </BlueprintShell>
      </MotionConfig>
    );
  }

  if (status === 'error') {
    return (
      <MotionConfig reducedMotion="user">
        <BlueprintShell>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
          >
            <div className="mb-8 text-center">
              <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                Steelman
              </span>
              <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
                That link didn't hold up.
              </h1>
              <p className="mt-2 text-sm text-ink-muted">
                It may have expired or already been used.
              </p>
            </div>

            <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 text-center shadow-frame backdrop-blur-sm">
              {errorDetail && (
                <p className="mb-5 rounded-lg border border-black/[0.08] bg-charcoal/[0.04] px-4 py-3 text-left text-sm text-charcoal">
                  {errorDetail}
                </p>
              )}

              <div className="space-y-3">
                <Link to="/confirm-email" className="btn-primary w-full">
                  Send a new link
                </Link>
                <Link to="/signin" className="btn-secondary w-full">
                  Try signing in
                </Link>
              </div>

              <p className="mt-4 text-xs text-ink-muted">
                If you already verified, signing in will work as usual.
              </p>
            </div>
          </motion.div>
        </BlueprintShell>
      </MotionConfig>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <BlueprintShell>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
        >
          <div className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
              Steelman
            </span>
            <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
              You're verified.
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              Your 3 free debate credits are ready.
            </p>
          </div>

          <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 text-center shadow-frame backdrop-blur-sm">
            {/* Check mark */}
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-black/[0.08] bg-charcoal/[0.04]">
              <motion.svg
                className="h-5 w-5 text-charcoal"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 0.3, duration: 0.45, ease: EASE }}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </motion.svg>
            </div>

            <div className="space-y-3">
              <Link to="/arena" className="btn-primary w-full">
                Start your first debate
              </Link>
              <Link to="/explore" className="btn-secondary w-full">
                Explore debates
              </Link>
            </div>

            <div className="mt-6">
              <div className="h-px w-full overflow-hidden bg-hairline">
                <motion.div
                  className="h-full bg-charcoal"
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: REDIRECT_SECONDS, ease: 'linear' }}
                />
              </div>
              <p className="mt-3 text-xs text-ink-muted">
                Taking you to the arena in {countdown}s
              </p>
            </div>
          </div>
        </motion.div>
      </BlueprintShell>
    </MotionConfig>
  );
};

export default EmailVerified;
